/** Deals reads — rec_deals, READ-ONLY (Phase 4a). Behind the SELECT policy
 * from MIGRATION_CRM2_PHASE4_READS.sql. Until that migration runs, PostgREST
 * returns an empty set (RLS: no policy = no rows) with a 200 — so "0 deals"
 * is ambiguous. We disambiguate with a HEAD count against the same table:
 * a 404/permission shape means "not opened yet", an honest 0 means "no deals".
 * The UI turns that into a clear message instead of a silent empty list. */
import { daysFromTodayAppTz } from "../../lib/apptime";
import { http, HttpError } from "../../lib/http";
import { pageAll } from "../edits/api";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";

export interface Deal {
  natKey: string;
  address: string;
  stageIdx: number;
  updatedAt: number;
  fields: Record<string, unknown>;
  /** card-level rehab (v1 c.rehab, set by the rehab-calculator sync) — the
   * FIRST link in v1's rehab chain, before NR_BY / lead edits. */
  rehab: number;
  /** v1 card deal label (Flip / Rental / BRRRR / Wholesale / Land), from the
   * Internal Assignment modal (⋯ on the board card). */
  label: string | null;
  /** v1 card internal-assignment name (c.partner.name). */
  partner: string | null;
  /** v1 c.partner.type — "Investor" | "Partner" (null = Unassigned). Optional:
   * always set by narrowDeal, omittable in hand-built Deal literals/fixtures. */
  partnerType?: string | null;
  /** v1 c.partner.color — one of ASSIGN_COLORS; drives the card pill hue. */
  partnerColor?: string | null;
  /** v1 c.partner.note — admin-only internal note. */
  partnerNote?: string | null;
  /** CRM-side names for Drive files, by Drive file id (Tom 2026-09-07: "name
   * them on the drive in the app… not the name in the drive itself"). The
   * Google Drive file is never renamed. */
  driveLabels?: Record<string, string>;
}

/** card.driveLabels → {fileId: label}; nulls (a cleared label) and junk dropped */
export function driveLabelsOf(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (v === null || typeof v !== "object" || Array.isArray(v)) return out;
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    if (k && typeof x === "string" && x.trim()) out[k] = x.trim();
  }
  return out;
}

const CACHE = "deals_v1";
const COLS = "nat_key,address,stage_idx,updated_at,fields,data";

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

export function narrowDeal(row: Record<string, unknown>): Deal | null {
  const nk = s(row["nat_key"]);
  if (!nk) return null;
  const updated = s(row["updated_at"]);
  const t = updated ? Date.parse(updated) : NaN;
  const f = row["fields"];
  const d = row["data"];
  const card = d !== null && typeof d === "object" && !Array.isArray(d) ? (d as Record<string, unknown>) : {};
  const rehabRaw = parseFloat(String(card["rehab"] ?? "").replace(/[^0-9.]/g, ""));
  const partnerRaw = card["partner"];
  const pObj = partnerRaw !== null && typeof partnerRaw === "object" && !Array.isArray(partnerRaw)
    ? (partnerRaw as Record<string, unknown>)
    : null;
  return {
    natKey: nk,
    address: s(row["address"]) ?? nk,
    stageIdx: typeof row["stage_idx"] === "number" ? row["stage_idx"] : 0,
    updatedAt: Number.isNaN(t) ? 0 : t,
    fields: f !== null && typeof f === "object" && !Array.isArray(f) ? (f as Record<string, unknown>) : {},
    rehab: Number.isFinite(rehabRaw) && rehabRaw > 0 ? rehabRaw : 0,
    label: s(card["label"]),
    partner: pObj ? s(pObj["name"]) : null,
    partnerType: pObj ? s(pObj["type"]) : null,
    partnerColor: pObj ? s(pObj["color"]) : null,
    partnerNote: pObj ? s(pObj["note"]) : null,
    driveLabels: driveLabelsOf(card["driveLabels"]),
  };
}

export interface DealsResult {
  deals: Deal[];
  dropped: number;
  ready: boolean; // false = the Phase 4 read migration hasn't been run yet
}

export async function cachedDeals(): Promise<Deal[] | null> {
  const c = await idbGet<{ at: number; deals: Deal[] }>(CACHE);
  return c && Array.isArray(c.deals) ? c.deals : null;
}

export async function fetchDeals(token: string, signal?: AbortSignal): Promise<DealsResult> {
  let rows: unknown[];
  try {
    // paged: PostgREST clamps limit to 1000 — a single shot silently truncates
    rows = await pageAll<unknown>(token, `rec_deals?select=${COLS}&deleted_at=is.null&order=updated_at.desc`, signal);
  } catch (e) {
    // 404 / undefined-table / permission = migration not run yet: say so plainly
    if (e instanceof HttpError && (e.status === 404 || e.status === 401 || e.status === 403)) {
      const cached = await cachedDeals();
      return { deals: cached ?? [], dropped: 0, ready: false };
    }
    throw e;
  }
  let dropped = 0;
  const deals: Deal[] = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const d = narrowDeal(r as Record<string, unknown>);
    if (!d) {
      dropped++;
      continue;
    }
    deals.push(d);
  }
  await idbSet(CACHE, { at: Date.now(), deals });
  return { deals, dropped, ready: true };
}

// ---- Phase 5: deal editing ---------------------------------------------------

const CAPS_KEY = "deals_caps_v1";

/** Can this deployment's database accept deals writes yet? Probed with a
 * `del` on a synthetic key — a no-op server-side when the key doesn't exist,
 * but it exercises the full whitelist: 200 = MIGRATION_CRM2_PHASE5_DEALS.sql
 * has been run; crm2_bad_ops = not yet. Cached per session. */
export async function probeDealsWrite(token: string): Promise<boolean> {
  const cached = await idbGet<{ at: number; open: boolean }>(CAPS_KEY);
  if (cached && cached.open) return true; // once open, always open
  try {
    await http(`${SUPABASE_URL}/rest/v1/rpc/crm2_apply_ops`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      body: { ops: [{ s: "deals", op: "del", k: "__crm2_capability_probe__" }] },
      attempts: 1,
      timeoutMs: 12000,
    });
    await idbSet(CAPS_KEY, { at: Date.now(), open: true });
    return true;
  } catch (e) {
    // Only the SERVER can say the migration hasn't run (an HTTP answer with
    // crm2_bad_ops). An unreachable server (2026-10-28: Supabase down, the
    // drawer announced "editing unlocks once MIGRATION_…_DEALS.sql runs" —
    // a wrong diagnosis on top of a real outage) says nothing about the
    // schema: keep what we knew, and in doubt stay open — the outbox queues
    // the edit and the server's own gate still refuses it if it must.
    if (!(e instanceof HttpError)) return cached ? cached.open : true;
    await idbSet(CAPS_KEY, { at: Date.now(), open: false });
    return false;
  }
}

/** Stage move: d must exist ({}), stage carries the target column. */
export function opMoveDealStage(natKey: string, stage: number): import("../../lib/outbox").RecOp {
  return { s: "deals", op: "patch", k: natKey, d: {}, stage };
}

/** Card money-field edit (t_purchase / t_arv / t_emd …): merged into the
 * card's edits map — v1's kanban shows them on its next poll. */
export function opEditDealField(natKey: string, field: string, value: string | null): import("../../lib/outbox").RecOp {
  return { s: "deals", op: "patch", k: natKey, d: { edits: { [field]: value } } };
}

/** Like opEditDealField but the value can be an array/object (Selling-AI stores
 * selRepairs / selDisclosure on the deal edits, v1's card.edits). Arrays replace
 * wholesale server-side; objects deep-merge — both correct for these blobs. */
export function opEditDealBlob(natKey: string, key: string, value: unknown): import("../../lib/outbox").RecOp {
  return { s: "deals", op: "patch", k: natKey, d: { edits: { [key]: value } } };
}

/** Internal Assignment (v1 saveAssign): the deal label + partner object live at
 * the card's TOP LEVEL — v1 sets `c.label` / `c.partner` directly and persists
 * the whole card, and `narrowDeal` reads them from the top of `data` (NOT from
 * edits/fields). Routing them through opEditDealBlob buried them in `data.edits`
 * (→ fields), where the board card never looks, so the pill reverted on the next
 * refresh. Deep-merge lands { label, partner } at `data.label` / `data.partner`;
 * a null partner replaces (clears) any prior assignment, matching v1 Unassigned. */
export function opAssignDeal(
  natKey: string,
  label: string | null,
  partner: { type: string; name: string; color: string; note: string } | null,
): import("../../lib/outbox").RecOp {
  return { s: "deals", op: "patch", k: natKey, d: { label, partner } };
}

/** Name a Drive file IN THE CRM (the file in Google Drive keeps its own name).
 * A nested patch — the door deep-merges `d` into the card, so one file's label
 * never touches another's; null clears it. */
export function opDealDriveLabel(natKey: string, fileId: string, label: string | null): import("../../lib/outbox").RecOp {
  return { s: "deals", op: "patch", k: natKey, d: { driveLabels: { [fileId]: label && label.trim() ? label.trim() : null } } };
}

/** v1 deleteTxnCard — soft-delete the deal. The 'del' op sets deleted_at server
 * side (the same tombstone every entity uses), so the row drops out of both v1's
 * and v2's `deleted_at is null` reads but stays recoverable. */
export function opDeleteDeal(natKey: string): import("../../lib/outbox").RecOp {
  return { s: "deals", op: "del", k: natKey };
}

/** v1's flip pipeline stage names, by column index — VERIFIED against v1
 * source: permits-roi.js `const TXN_COLS=[...]` (the template the blob
 * recompose reuses: supabase-sync.js `_recBootReadDeals` places rec_deals
 * rows into TXN_COLS clones by stage_idx). Earlier v2 builds showed invented
 * names here ("Lead", "Under Contract", …) — same indexes, wrong labels. */
export const STAGE_NAMES = [
  "Contract Signed / Pending", // 0
  "Closed",                    // 1
  "Rehab",                     // 2
  "On Market",                 // 3
  "Buyer Inspection",          // 4
  "Pending Sale",              // 5
  "Sold",                      // 6
];
export const stageName = (i: number): string => STAGE_NAMES[i] ?? `Stage ${i + 1}`;

/** v1 stores edited dates as display strings ("Jul 31, 2026") — commitEdit
 * runs the date input's ISO through _fromISO before saving. Mirror that. */
export function fmtDateEdit(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso + "T00:00:00");
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** A stored deal date → LOCAL midnight, or null when unset/unparseable.
 *
 * Local midnight matters: v1 stores display strings ("Jul 31, 2026"), which
 * Date.parse reads as local, but a bare ISO date ("2026-07-31") is parsed as
 * UTC — so west of Greenwich `new Date("2026-07-31")` is Jul 30 locally and
 * every countdown would read one day short. ISO is therefore split by hand. */
export function parseDealDate(v: unknown): Date | null {
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s || s === "—") return null; // v1 writes an em dash for "no date"
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole days from today to a stored deal date (negative = already past).
 * "Today" is EASTERN, not the device's timezone — a teammate on a laptop set
 * to Pacific must see the same "3 days to closing" Tom does. See lib/apptime. */
export function daysUntil(v: unknown, now: Date = new Date()): number | null {
  const target = parseDealDate(v);
  if (!target) return null;
  return daysFromTodayAppTz(target, now);
}

/** Add days to a stored deal date and return it in v1's display format —
 * the format every due date in the app is written in. "" when unparseable. */
export function shiftDealDate(v: unknown, days: number): string {
  const d = parseDealDate(v);
  if (!d) return "";
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
  return out.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** "in 12 days" / "tomorrow" / "today" / "2 days ago" — the countdown wording
 * used on the board cards. */
export function countdownLabel(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days > 0) return `in ${days} days`;
  return `${-days} days ago`;
}

/** Urgency bucket for the card pill: past → grey-red, today → red, within a
 * week → amber, further out → calm. */
export function countdownTone(days: number): "past" | "now" | "soon" | "far" {
  if (days < 0) return "past";
  if (days === 0) return "now";
  if (days <= 7) return "soon";
  return "far";
}

/** Best-effort reverse: turn a stored v1 date string back into yyyy-mm-dd for
 * an <input type="date">; unparseable → "" (input shows empty, keeps stored).
 *
 * FIXED 2026-07-28: this used `new Date(v)` directly. For a value already
 * stored as a bare ISO date that parses as UTC midnight, so west of Greenwich
 * getDate() returned the DAY BEFORE — open a date field, touch nothing, save,
 * and the date silently moved back one day. parseDealDate reads date-only
 * values as local midnight, which round-trips exactly. */
export function toDateInput(v: string | null): string {
  if (!v) return "";
  const d = parseDealDate(v);
  if (!d) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** v1's FULL deal-card field registry — every ef('t_…') in transactions.js,
 * exact labels; kinds follow v1: 'date' fields used ef(…,'date'), money via
 * _isMoneyEditKey, Yes/No trio (estoppel/lien/title search) default 'No'. */
export interface DealField {
  key: string;
  label: string;
  kind: "money" | "text" | "date" | "yesno";
  group: "numbers" | "timeline" | "hoa" | "utilities" | "insurance" | "title" | "dispo" | "access" | "rehab" | "access2" | "sellist" | "selagent" | "selbuyer" | "propinfo" | "propfin";
}

export const DEAL_FIELDS: DealField[] = [
  // v1 transactions.js Purchasing tab → "Purchasing" (ef('t_*',…)), v1 field order
  { key: "t_purchase", label: "Purchase Price", kind: "money", group: "numbers" },
  { key: "t_arv", label: "ARV", kind: "money", group: "numbers" }, // v2 addition, paired with Purchase Price
  { key: "t_closing", label: "Closing Date", kind: "date", group: "numbers" },
  { key: "t_emd", label: "EMD", kind: "money", group: "numbers" },
  { key: "t_lenderLtv", label: "Lender Approved %", kind: "text", group: "numbers" },
  { key: "t_conting", label: "Contingency", kind: "text", group: "numbers" },
  { key: "t_lockbox", label: "Lockbox Code", kind: "text", group: "numbers" },
  { key: "t_vacancy", label: "Vacancy Date", kind: "date", group: "numbers" },
  { key: "t_postocc", label: "Post Occupancy End Date", kind: "date", group: "numbers" },
  // v1 Purchasing tab → "Inspection & Timeline"
  { key: "t_inspend", label: "Inspection Period Ends", kind: "date", group: "timeline" },
  { key: "t_inspdate", label: "Inspection Date", kind: "date", group: "timeline" },
  { key: "t_walk", label: "Walkthrough Date", kind: "date", group: "timeline" },
  // v1 Purchasing tab → "Title"
  { key: "t_titleco", label: "Title Company", kind: "text", group: "title" }, // v2 (v1 shows in dispo)
  // 2026-08-31 (Tom): the convert gate REQUIRED title phone/email + dispo on
  // the lead, then the deal had nowhere to hold them. They now live here and
  // are seeded on convert (see convert/api.ts flipCardFor).
  { key: "t_titlephone", label: "Title Phone", kind: "text", group: "title" },
  { key: "t_titleemail", label: "Title Email", kind: "text", group: "title" },
  { key: "t_title", label: "Title Search Clear", kind: "yesno", group: "title" },
  { key: "t_lien", label: "Lien Search Clear", kind: "yesno", group: "title" },
  { key: "t_estoppel", label: "Estoppel Received", kind: "yesno", group: "title" },
  // Disposition contact (2026-08-31, Tom: "title information and dispo
  // contact info isnt transferred to the transaction") — the wholesaler /
  // agent we're buying THROUGH, carried over from the lead on convert.
  { key: "t_dispco", label: "Dispo Company", kind: "text", group: "dispo" },
  { key: "t_dispname", label: "Dispo Name", kind: "text", group: "dispo" },
  { key: "t_dispphone", label: "Dispo Phone", kind: "text", group: "dispo" },
  { key: "t_dispemail", label: "Dispo Email", kind: "text", group: "dispo" },
  // v1 Purchasing tab → "Billing Info" (website then account, per v1).
  // Company / username / password rows added 2026-08-11 (Tom: "add few
  // fields") — company first, then site, then the login pair, per utility.
  { key: "t_elecco", label: "Electric Company", kind: "text", group: "utilities" },
  { key: "t_elecweb", label: "Electric Website", kind: "text", group: "utilities" },
  { key: "t_elecacct", label: "Electric Account Info", kind: "text", group: "utilities" },
  { key: "t_elecuser", label: "Electric Account Username", kind: "text", group: "utilities" },
  { key: "t_elecpass", label: "Electric Account Password", kind: "text", group: "utilities" },
  { key: "t_waterco", label: "Water Company", kind: "text", group: "utilities" },
  { key: "t_waterweb", label: "Water Website", kind: "text", group: "utilities" },
  { key: "t_wateracct", label: "Water Account Info", kind: "text", group: "utilities" },
  { key: "t_wateruser", label: "Water Account Username", kind: "text", group: "utilities" },
  { key: "t_waterpass", label: "Water Account Password", kind: "text", group: "utilities" },
  // v1 Purchasing tab → "Insurance Info"
  { key: "t_insco", label: "Insurance Company Name", kind: "text", group: "insurance" },
  { key: "t_insexp", label: "Insurance Expiration Date", kind: "date", group: "insurance" },
  // v1 Purchasing tab → "HOA Info"
  { key: "t_hoaco", label: "HOA Company Name", kind: "text", group: "hoa" },
  { key: "t_hoafee", label: "HOA Fee Amount", kind: "money", group: "hoa" },
  { key: "t_hoafreq", label: "HOA Fee Frequency", kind: "text", group: "hoa" },
  // v1 transactions.js Rehab tab → "Rehab Timeline" (ef('r_*',…))
  { key: "r_start", label: "Start Rehab Date", kind: "date", group: "rehab" },
  { key: "r_pfinish", label: "Projected Finish Date", kind: "date", group: "rehab" },
  { key: "r_afinish", label: "Actual Finish Date", kind: "date", group: "rehab" },
  { key: "r_budget", label: "Estimated Budget", kind: "money", group: "rehab" },
  { key: "r_spend", label: "Actual Spend", kind: "money", group: "rehab" },
  { key: "r_pct", label: "% Complete", kind: "text", group: "rehab" },
  // v1 Rehab tab → "Property Access FAQ"
  { key: "r_lockbox", label: "Lockbox Code", kind: "text", group: "access2" },
  { key: "r_garage", label: "Garage Code", kind: "text", group: "access2" },
  { key: "r_roof", label: "Roof Age", kind: "text", group: "access2" },
  { key: "r_roofw", label: "Roof Warranty", kind: "text", group: "access2" },
  { key: "r_hvac", label: "HVAC Age", kind: "text", group: "access2" },
  { key: "r_hwh", label: "HWH Age", kind: "text", group: "access2" },
  { key: "r_septic", label: "Septic/Sewer", kind: "text", group: "access2" },
  // v1 transactions.js Selling tab → "Details" → "Listing Information"
  { key: "sl_ldate", label: "Listing Date", kind: "date", group: "sellist" },
  { key: "sl_lprice", label: "Listing Price", kind: "money", group: "sellist" },
  { key: "sl_mls", label: "MLS Number", kind: "text", group: "sellist" },
  { key: "sl_cprice", label: "Contract Price", kind: "money", group: "sellist" },
  { key: "sl_sell", label: "Actual Sell Price", kind: "money", group: "sellist" },
  { key: "sl_concession", label: "Seller Concession", kind: "money", group: "sellist" },
  // v1 Selling → "Details" → "Listing Agent"
  { key: "sl_aname", label: "Agent Name", kind: "text", group: "selagent" },
  { key: "sl_aphone", label: "Agent Phone", kind: "text", group: "selagent" },
  { key: "sl_aemail", label: "Agent Email", kind: "text", group: "selagent" },
  // v1 Selling → "Details" → "Buyer Details"
  { key: "sl_bsig", label: "Buyer Signature Date", kind: "date", group: "selbuyer" },
  { key: "sl_binsp", label: "Buyer Inspection Period", kind: "text", group: "selbuyer" },
  { key: "sl_bdep", label: "Buyer Deposit Amount", kind: "money", group: "selbuyer" },
  { key: "sl_bdeprec", label: "Buyer Deposit Received", kind: "text", group: "selbuyer" },
  { key: "sl_binspdt", label: "Buyer Inspection Date & Time", kind: "date", group: "selbuyer" },
  { key: "sl_bappr", label: "Buyer Appraisal Date & Time", kind: "date", group: "selbuyer" },
  { key: "sl_bclose", label: "Buyer Closing Date", kind: "date", group: "selbuyer" },
  // v1 Property tab → "Property Info" (ef('pp_*'))
  { key: "pp_addr", label: "Address", kind: "text", group: "propinfo" },
  { key: "pp_city", label: "City", kind: "text", group: "propinfo" },
  { key: "pp_zip", label: "ZIP", kind: "text", group: "propinfo" },
  { key: "pp_county", label: "County", kind: "text", group: "propinfo" },
  { key: "pp_subdiv", label: "Subdivision", kind: "text", group: "propinfo" },
  { key: "pp_parcel", label: "Parcel ID", kind: "text", group: "propinfo" },
  { key: "pp_type", label: "Property Type", kind: "text", group: "propinfo" },
  { key: "pp_yr", label: "Year Built", kind: "text", group: "propinfo" },
  { key: "pp_sqft", label: "Sqft", kind: "text", group: "propinfo" },
  { key: "pp_lot", label: "Lot Size", kind: "text", group: "propinfo" },
  { key: "pp_beds", label: "Bedrooms", kind: "text", group: "propinfo" },
  { key: "pp_baths", label: "Bathrooms", kind: "text", group: "propinfo" },
  { key: "pp_pool", label: "Pool", kind: "text", group: "propinfo" },
  { key: "pp_garage", label: "Garage", kind: "text", group: "propinfo" },
  { key: "pp_sink", label: "Sinkhole", kind: "text", group: "propinfo" },
  // v1 Property tab → "Property Financials"
  { key: "pp_ask", label: "Asking Price", kind: "money", group: "propfin" },
  { key: "pp_offer", label: "Offer Price", kind: "money", group: "propfin" },
  { key: "pp_purch", label: "Purchase Price", kind: "money", group: "propfin" },
  { key: "pp_contract", label: "Contract Price", kind: "money", group: "propfin" },
  { key: "pp_repairs", label: "Repairs Estimate", kind: "money", group: "propfin" },
  { key: "pp_emd", label: "EMD", kind: "money", group: "propfin" },
  { key: "pp_larv", label: "Listed ARV", kind: "money", group: "propfin" },
  { key: "pp_carv", label: "Comped ARV", kind: "money", group: "propfin" },
  { key: "pp_lprice", label: "Listing Price", kind: "money", group: "propfin" },
  { key: "pp_mort", label: "Mortgage Left", kind: "money", group: "propfin" },
];

export const DEAL_GROUPS: { id: DealField["group"]; label: string }[] = [
  { id: "numbers", label: "Purchasing" },        // v1 transactions.js Purchasing section
  { id: "timeline", label: "Inspection & Timeline" },
  { id: "rehab", label: "Rehab Timeline" },      // v1 Rehab tab → Rehab Timeline
  { id: "access2", label: "Property Access FAQ" }, // v1 Rehab tab → Property Access FAQ
  { id: "hoa", label: "HOA Info" },
  { id: "utilities", label: "Billing Info" },
  { id: "insurance", label: "Insurance Info" },
  { id: "title", label: "Title" },
  { id: "dispo", label: "Disposition Contact" },
  { id: "access", label: "Property Access" },
  { id: "propinfo", label: "Property Info" },       // v1 Property tab
  { id: "propfin", label: "Property Financials" },
  // v1 Selling tab → Details splits into three labeled sections
  { id: "sellist", label: "Listing Information" },
  { id: "selagent", label: "Listing Agent" },
  { id: "selbuyer", label: "Buyer Details" },
];

/** v1 relatedVendorsSection (txn-expenses.js): a deal's "Related Vendors" list —
 * `c.edits.relVendors = [{name, type}]`. Whole-array replace through the deals
 * door (onSaveDealBlob → opEditDealBlob), read back off deal.fields.relVendors. */
export interface RelVendor {
  name: string;
  type: string;
}

/** v1 Selling-tab computed read-outs: inspection-period end date (buyer
 * signature + inspection-period days) and days-to-pending (listing → signature). */
/** FIXED 2026-07-28 (found by tests/dealDetail.test.ts failing only outside
 * UTC): this used `Date.parse` + millisecond arithmetic. A buyer signature
 * stored as a bare ISO date parses as UTC midnight, so "2026-07-01" + 10 days
 * landed at Jul 11 00:00 UTC = Jul 10, 8pm Eastern — the Selling tab has been
 * showing the inspection period ending ONE DAY EARLY in Tom's timezone, while
 * CI (UTC) saw the expected date and stayed green. Date-only values are now
 * parsed to local midnight and shifted by CALENDAR days, which is also DST
 * safe: adding 86400000ms across a 23-hour day loses an hour, and enough of
 * those slip a date. */
export function dealSellingComputed(fields: Record<string, unknown>): { inspEnd: string; daysToPending: string } {
  const g = (k: string) => (typeof fields[k] === "string" ? (fields[k] as string) : "");
  const sig = parseDealDate(g("sl_bsig"));
  const days = parseInt(g("sl_binsp").replace(/[^0-9]/g, ""), 10);
  let inspEnd = "";
  if (sig && Number.isFinite(days) && days > 0) {
    inspEnd = new Date(sig.getFullYear(), sig.getMonth(), sig.getDate() + days)
      .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  const ld = parseDealDate(g("sl_ldate"));
  let daysToPending = "";
  if (ld && sig) daysToPending = String(Math.max(0, Math.round((sig.getTime() - ld.getTime()) / 86400000))) + " days";
  return { inspEnd, daysToPending };
}

/** Deal Contacts (v1 transactions.js CONTACTS sidebar): derived, never a stored
 * per-deal list. Seller/Dispo/Title come off the LINKED LEAD's edits
 * (lead_fields) — seller as v1's {name,role,phone,email,company} object,
 * dispo/title as flat fields — with the deal's own t_titleco as a title
 * fallback. Empty contacts are omitted. */
export interface DealContact {
  role: string;
  name: string;
  company: string;
  phone: string;
  email: string;
}

const cstr = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

export function dealContacts(
  leadEdits: Record<string, unknown> | undefined,
  dealFields: Record<string, unknown>,
): DealContact[] {
  const le = leadEdits ?? {};
  const out: DealContact[] = [];

  const sellerRaw = le["seller"];
  if (sellerRaw && typeof sellerRaw === "object" && !Array.isArray(sellerRaw)) {
    const o = sellerRaw as Record<string, unknown>;
    const c: DealContact = {
      role: cstr(o["role"]) || "Seller",
      name: cstr(o["name"]),
      company: cstr(o["company"]),
      phone: cstr(o["phone"]),
      email: cstr(o["email"]),
    };
    if (c.name || c.phone || c.email) out.push(c);
  }

  const disp: DealContact = {
    role: "Dispo",
    name: cstr(le["dispName"]),
    company: cstr(le["dispCompany"]),
    phone: cstr(le["dispPhone"]),
    email: cstr(le["dispEmail"]),
  };
  if (disp.name || disp.company || disp.phone || disp.email) out.push(disp);

  const title: DealContact = {
    role: "Title",
    name: "",
    company: cstr(le["titleCo"]) || cstr(dealFields["t_titleco"]),
    phone: cstr(le["titlePhone"]),
    email: cstr(le["titleEmail"]),
  };
  if (title.company || title.phone || title.email) out.push(title);

  return out;
}

/** Deal timeline (v2 derived, read-only): the deal's filled date fields
 * (closing, inspection, rehab start/finish …) in chronological order — a real,
 * useful "activity/timeline" from stored deal data (v1's Activity tab was a
 * never-written stub, so this improves on it rather than copying dead code). */
export interface TimelineEntry {
  label: string;
  date: string;
  ts: number;
}

export function dealTimeline(fields: Record<string, unknown>): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  for (const f of DEAL_FIELDS) {
    if (f.kind !== "date") continue;
    const v = fields[f.key];
    if (typeof v !== "string" || !v.trim()) continue;
    const ts = Date.parse(v);
    out.push({ label: f.label, date: v, ts: Number.isNaN(ts) ? 0 : ts });
  }
  return out.sort((a, b) => a.ts - b.ts);
}

// ---- Deal showings + team + chat (v1 flip Showings/Team tabs, on deal edits) --

export interface DealShowing { id: string; buyer: string; ph: string; dt: string; status: string; access: string; feedback: string; }
export interface DealTeamMember { n: string; role: string; }
export interface DealChatMsg { who: string; m: string; }
export const SHOWING_STATUS = ["Scheduled", "Completed", "Cancelled", "No-Show"];

let _sid = 0;
export const dealShowingId = (): string => "sh" + Date.now().toString(36) + (_sid++).toString(36);

const ds = (v: unknown): string => (typeof v === "string" ? v : "");

export function narrowDealShowings(raw: unknown): DealShowing[] {
  if (!Array.isArray(raw)) return [];
  const out: DealShowing[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    if (!ds(o["buyer"]).trim() && !ds(o["dt"]).trim()) continue;
    out.push({ id: ds(o["id"]) || dealShowingId(), buyer: ds(o["buyer"]), ph: ds(o["ph"]), dt: ds(o["dt"]), status: ds(o["status"]) || "Scheduled", access: ds(o["access"]), feedback: ds(o["feedback"]) });
  }
  return out;
}
export function narrowDealTeam(raw: unknown): DealTeamMember[] {
  if (!Array.isArray(raw)) return [];
  const out: DealTeamMember[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const n = ds(o["n"]).trim();
    if (!n) continue;
    out.push({ n, role: ds(o["role"]).trim() || "Member" });
  }
  return out;
}
export function narrowDealChat(raw: unknown): DealChatMsg[] {
  if (!Array.isArray(raw)) return [];
  const out: DealChatMsg[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const m = ds(o["m"]).trim();
    if (!m) continue;
    out.push({ who: ds(o["who"]).trim() || "?", m });
  }
  return out;
}

export function narrowRelVendors(raw: unknown): RelVendor[] {
  if (!Array.isArray(raw)) return [];
  const out: RelVendor[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const name = typeof o["name"] === "string" ? o["name"].trim() : "";
    if (!name) continue;
    out.push({ name, type: typeof o["type"] === "string" ? o["type"].trim() : "" });
  }
  return out;
}
